import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { STATUS_META } from "../../pages/site/seo/constants";
import { sitePath } from "../../utils/urls";
import {
  changePercent,
  formatChange,
  formatCountLabel,
  formatValue,
} from "./format";
import { resolveSiteMetric } from "./metrics";
import PrivacyMask from "./PrivacyMask";

const CHART_TOOLTIP_STYLE = {
  backgroundColor: "var(--theme-main-bg)",
  border: "1px solid var(--theme-shadow)",
  borderRadius: "10px",
  color: "var(--theme-text)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};

const SiteCard = ({
  site,
  view,
  metric,
  privacy,
  hero = false,
  layout = "grid",
}) => {
  const navigate = useNavigate();
  const resolved = resolveSiteMetric(site, view, metric);
  const change = changePercent(resolved.value, resolved.previous);
  const isList = layout === "list" && !hero;

  return (
    <button
      type="button"
      onClick={() => navigate(sitePath(site, "analytics/"))}
      className={`group relative z-0 flex w-full overflow-visible bg-theme-main text-left transition-transform hover:z-20 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-theme-primary ${
        isList
          ? "flex-row items-center gap-4 rounded-xl border border-theme-secondary p-3"
          : `flex-col rounded-2xl border border-theme-secondary p-4 ${hero ? "min-h-[280px]" : "min-h-[200px]"}`
      }`}
    >
      <div className={`flex items-start justify-between gap-2 ${isList ? "w-48 shrink-0" : ""}`}>
        <div className="min-w-0">
          <Hostname hostname={site.hostname || site.name} privacy={privacy} />
          {!privacy && (
            <div className="mt-0.5 truncate text-[11px] text-theme-text opacity-50">
              {site.name}
            </div>
          )}
        </div>
        {!isList && (
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold uppercase tracking-wide text-theme-text">
              {headline(resolved)}
            </div>
            {change != null && (
              <div className={`text-xs font-medium ${changeClassName(change)}`}>
                {formatChange(change)}
              </div>
            )}
          </div>
        )}
      </div>

      {isList ? (
        <>
          <div className="h-10 min-w-0 flex-1 overflow-visible [&_.recharts-wrapper]:!overflow-visible">
            <Sparkline site={site} view={view} resolved={resolved} height={40} />
          </div>
          <div className="w-28 text-right shrink-0">
            <div className="text-sm font-semibold text-theme-text">
              {headline(resolved)}
            </div>
            {change != null && (
              <div className={`text-xs font-medium ${changeClassName(change)}`}>
                {formatChange(change)}
              </div>
            )}
          </div>
          <div className="hidden sm:flex w-40 justify-end gap-3 text-[11px] text-theme-text opacity-60">
            {secondaryStats(site, view, resolved).map((stat) => (
              <span key={stat.label}>
                <span className="opacity-70">{stat.label}</span> {stat.value}
              </span>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className={`mt-3 flex-1 overflow-visible [&_.recharts-wrapper]:!overflow-visible ${hero ? "h-36" : "h-20"}`}>
            <Sparkline site={site} view={view} resolved={resolved} height={hero ? 144 : 80} />
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-theme-text opacity-65">
            {secondaryStats(site, view, resolved).map((stat) => (
              <span key={stat.label}>
                <span className="font-medium opacity-80">{stat.label}</span>{" "}
                {stat.value}
              </span>
            ))}
          </div>
        </>
      )}
    </button>
  );
};

const Hostname = ({ hostname, privacy }) => {
  if (privacy) {
    return <PrivacyMask hostname={hostname} />;
  }
  return (
    <div className="truncate text-sm font-medium text-theme-text">
      {hostname || "Untitled site"}
    </div>
  );
};

const Sparkline = ({ site, view, resolved, height }) => {
  if (view === "audits" && resolved.id === "pass_rate") {
    const data = site.series || [];
    if (data.length < 1) {
      return <EmptyChart label="No audit reports yet" />;
    }
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} stackOffset="none">
          <Tooltip
            content={<SparklineTooltip resolved={resolved} stacked />}
            cursor={{ fill: "var(--theme-shadow)", opacity: 0.25 }}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: 30, overflow: "visible", pointerEvents: "none" }}
          />
          <Bar dataKey="fail" stackId="a" fill={STATUS_META.fail.fill} isAnimationActive={false} />
          <Bar dataKey="warn" stackId="a" fill={STATUS_META.warn.fill} isAnimationActive={false} />
          <Bar dataKey="pass" stackId="a" fill={STATUS_META.pass.fill} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (view === "indexing") {
    return <EmptyChart label="Connect Google Search Console" />;
  }

  const data = resolved.series || [];
  if (data.length < 2) {
    return <EmptyChart label={view === "growth" && resolved.id === "pages" ? "Snapshot only" : "No data yet"} />;
  }

  const stroke = view === "speed"
    ? ratingColor((site.extra || {}).rating)
    : "var(--theme-text)";

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <Tooltip
          content={<SparklineTooltip resolved={resolved} />}
          cursor={{ stroke: "var(--theme-text)", strokeOpacity: 0.25 }}
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 30, overflow: "visible", pointerEvents: "none" }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={stroke}
          fill={stroke}
          fillOpacity={0.12}
          strokeWidth={1.75}
          isAnimationActive={false}
          activeDot={{ r: 3, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const SparklineTooltip = ({ active, payload, resolved, stacked }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const dateLabel = formatSeriesDate(point.date);
  return (
    <div className="px-2.5 py-2 text-xs" style={CHART_TOOLTIP_STYLE}>
      {dateLabel && (
        <div className="mb-1 text-theme-text opacity-55">{dateLabel}</div>
      )}
      {stacked ? (
        <div className="space-y-0.5 text-theme-text">
          <div><span className="opacity-60">Pass</span> {point.pass ?? 0}</div>
          <div><span className="opacity-60">Warn</span> {point.warn ?? 0}</div>
          <div><span className="opacity-60">Fail</span> {point.fail ?? 0}</div>
        </div>
      ) : (
        <div className="font-medium text-theme-text">
          {formatTooltipValue(point.value, resolved)}
        </div>
      )}
    </div>
  );
};

const formatSeriesDate = (value) => {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatTooltipValue = (value, resolved) => {
  if (resolved.unit) return formatCountLabel(value, resolved.unit);
  return formatValue(value, resolved.format || "number");
};

const EmptyChart = ({ label }) => (
  <div className="flex h-full items-center justify-center rounded-lg bg-black bg-opacity-5 text-[11px] text-theme-text opacity-50">
    {label}
  </div>
);

const headline = (resolved) => {
  if (resolved.unit) return formatCountLabel(resolved.value, resolved.unit);
  return formatValue(resolved.value, resolved.format || "number");
};

const secondaryStats = (site, view, resolved) => {
  const extra = site.extra || {};
  if (view === "traffic") {
    return [
      { label: "VISITS", value: formatValue(site.value, "number") },
      { label: "PV", value: formatValue(extra.pageviews, "number") },
      { label: "P/V", value: formatValue(
        site.value ? (extra.pageviews || 0) / site.value : 0,
        "decimal",
      ) },
    ].filter((stat) => {
      if (resolved.id === "visits") return stat.label !== "VISITS";
      if (resolved.id === "pageviews") return stat.label !== "PV";
      if (resolved.id === "pages_per_visit") return stat.label !== "P/V";
      return true;
    });
  }
  if (view === "organic") {
    return [
      { label: "SEARCH", value: formatValue(extra.search_visits ?? site.value, "number") },
      { label: "AI", value: formatValue(extra.ai_visits || 0, "number") },
    ].filter((stat) => {
      if (resolved.id === "search") return stat.label !== "SEARCH";
      if (resolved.id === "ai") return stat.label !== "AI";
      return true;
    });
  }
  if (view === "speed") {
    return [
      { label: "GOOD", value: formatValue(site.value, "percent") },
      { label: "LCP", value: formatValue(extra.lcp, "ms") },
      { label: "INP", value: formatValue(extra.inp, "ms") },
      { label: "CLS", value: extra.cls != null ? formatValue(extra.cls, "cls") : "N/A" },
    ].filter((stat) => {
      if (resolved.id === "good_lcp") return stat.label !== "GOOD";
      if (resolved.id === "lcp") return stat.label !== "LCP";
      if (resolved.id === "inp") return stat.label !== "INP";
      if (resolved.id === "cls") return stat.label !== "CLS";
      return true;
    });
  }
  if (view === "audits") {
    return [
      { label: "PASS", value: extra.pass ?? 0 },
      { label: "WARN", value: extra.warn ?? 0 },
      { label: "FAIL", value: extra.fail ?? 0 },
    ];
  }
  if (view === "growth") {
    return [
      { label: "PAGES", value: formatValue(site.value, "number") },
      { label: "DEPLOYS", value: extra.deploy_count ?? 0 },
      {
        label: "LAST",
        value: extra.last_deploy_at
          ? new Date(extra.last_deploy_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })
          : "N/A",
      },
    ].filter((stat) => {
      if (resolved.id === "pages") return stat.label !== "PAGES";
      if (resolved.id === "deploys") return stat.label !== "DEPLOYS";
      return true;
    });
  }
  return [{ label: "STATUS", value: "Soon" }];
};

const changeClassName = (change) => {
  if (change > 0) return "text-emerald-700";
  if (change < 0) return "text-red-600";
  return "opacity-50";
};

const ratingColor = (rating) => {
  if (rating === "good") return STATUS_META.pass.fill;
  if (rating === "needs_improvement") return STATUS_META.warn.fill;
  if (rating === "poor") return STATUS_META.fail.fill;
  return "var(--theme-text)";
};

export default SiteCard;
